import { z } from 'zod';

// Supporting schemas (minimal, expand as needed)
const SuggestedTipSchema = z.object({
  isDefault: z.boolean(),
  percentage: z.number(),
});

const OrderTypeSchema = z.object({
  orderTypeRef: z.number(),
  name: z.string(),
  serviceLevelTime: z.number().optional(),
  suggestedTips: z.array(SuggestedTipSchema).optional(),
});

const OrderChannelSchema = z.object({
  orderChannelId: z.number(),
  name: z.record(z.string()), // e.g. { "en-US": "Default" }
  isDefault: z.boolean(),
  serviceLevelTime: z.number().optional(),
});

const PostalAddressSchema = z.object({
  addressLine1: z.string(),
  addressLine2: z.string().optional(),
  floor: z.string().optional(),
  locality: z.string(),
  region: z.string(),
  postalCode: z.string(),
  country: z.string(),
  notes: z.string().optional(),
});

const GeoLocationSchema = z.object({
  latitude: z.number(),
  longitude: z.number(),
});

const ExtensionDataSchema = z.unknown(); // Define as needed
const PhoneNumberSchema = z.string(); // Or a more specific schema if needed

export const SimphonyRevenueCenterSchema = z.object({
  orgShortName: z.string(),
  locRef: z.string(),
  rvcRef: z.number(),
  name: z.string(),
  orderTypes: z.array(OrderTypeSchema).optional(),
  orderChannels: z.array(OrderChannelSchema).optional(),
  tables: z.array(z.string()).optional(),
  consumerName: z.string().optional(),
  address: PostalAddressSchema.optional(),
  geoLocation: GeoLocationSchema.optional(),
  extensions: ExtensionDataSchema.optional(),
  phoneNumber: PhoneNumberSchema.optional(),
});

export type SimphonyRevenueCenter = z.infer<typeof SimphonyRevenueCenterSchema>;

export const SimphonyMenuItemSchema = z.object({
  menuItemId: z.number(),
  definitionSequence: z.number(),
  name: z.object({
    'en-US': z.string(),
  }),
  quantity: z.number(),
  unitPrice: z.number(),
  priceSequence: z.number(),
  total: z.number(),
  seat: z.number(),
  referenceText: z.string(),
  surcharge: z.number(),
  itemDiscounts: z.array(z.any()),
  condiments: z.array(z.any()),
  extensions: z.array(z.any()),
});

export type SimphonyMenuItem = z.infer<typeof SimphonyMenuItemSchema>;

export const SimphonyMenuCategorySchema = z.object({
  id: z.number(),
  name: z.string(),
  items: z.array(SimphonyMenuItemSchema),
});

export type SimphonyMenuCategory = z.infer<typeof SimphonyMenuCategorySchema>;

export const MenuPriceSchema = z.object({
  price: z.number(),
  priceSequence: z.number(),
});

export const CondimentGroupRuleSchema = z.object({
  condimentGroupRef: z.number(),
  minimumCount: z.number(),
  maximumCount: z.number(),
});

export const SimphonyMenuDefinitionSchema = z.object({
  name: z.record(z.string()),
  prices: z.array(MenuPriceSchema),
  condimentGroupRules: z.array(CondimentGroupRuleSchema).optional(),
  allergens: z.array(z.string()).optional(),
});

export type SimphonyMenuDefinition = z.infer<typeof SimphonyMenuDefinitionSchema>;

export const SimphonyMenuItemDetailSchema = SimphonyMenuItemSchema.extend({
  familyGroupRef: z.number(),
  definitions: z.array(SimphonyMenuDefinitionSchema),
});

export type SimphonyMenuItemDetail = z.infer<typeof SimphonyMenuItemDetailSchema>;

export const condimentItemSchema = z.object({
  condimentId: z.number(),
  name: z.record(z.string()),
});

export const SimphonyMenuDetailSchema = z.object({
  menuId: z.string(),
  menuItems: z.array(SimphonyMenuItemDetailSchema),
  condimentItems: z.array(condimentItemSchema),
});

export type SimphonyMenuDetail = z.infer<typeof SimphonyMenuDetailSchema>;

export const SimphonyMenuSummarySchema = z.object({
  categories: z.array(SimphonyMenuCategorySchema),
  items: z.array(SimphonyMenuItemSchema),
  status: z.string(),
  message: z.string().optional(),
});

export type SimphonyMenuSummary = z.infer<typeof SimphonyMenuSummarySchema>;

export const SimphonyTenderItemSchema = z.object({
  tenderId: z.number(),
  name: z.string(),
  tenderMediaId: z.number(),
  tenderMediaRef: z.number(),
  tenderMediaObjectNumber: z.number(),
});

export const SimphonyTenderCollectionSchema = z.object({
  items: z.array(SimphonyTenderItemSchema),
  status: z.string(),
  message: z.string().optional(),
});

export type SimphonyTenderItem = z.infer<typeof SimphonyTenderItemSchema>;
export type SimphonyTenderCollection = z.infer<typeof SimphonyTenderCollectionSchema>;
